import { JSONContent } from '@tiptap/core';
import { ChartType } from 'modules/Editor/components/WidgetMenu/SideMenu/ChartsWidgetMenu/ChartsWidgetMenu.config';
import { Subset } from 'types/object.types';
import { BasicWidgetTypes, WidgetType } from 'types/widget.types';
import { isTransparent, toHex } from 'utils/color';
import { ScatterBubbleChartWidgetData } from 'widgets/ChartWidgets/ScatterBubbleChartWidget/ScatterBubbleChartWidget.types';

import { BasicShapeWidgetData } from './BasicShapeWidget/BasicShapeWidget.types';
import { AreaChartWidgetData } from './ChartWidgets/Area/AreaChartWidget.types';
import { AreaSplineChartWidgetData } from './ChartWidgets/AreaSplineChartWidget/AreaSplineChartWidget.types';
import { BarChartWidgetData } from './ChartWidgets/BarChartWidget/BarChartWidget.types';
import { CloudBubbleChartWidgetData } from './ChartWidgets/CloudBubbleChartWidget/CloudBubbleChartWidget.types';
import { ColumnChartWidgetData } from './ChartWidgets/ColumnChartWidget/ColumnChartWidget.types';
import { ComboChartWidgetData } from './ChartWidgets/ComboChartWidget/ComboChartWidget.types';
import { FunnelChartWidgetData } from './ChartWidgets/FunnelChartWidget/FunnelChartWidget.types';
import { HalfPieChartWidgetData } from './ChartWidgets/HalfPieChartWidget/HalfPieChartWidget.types';
import { HistogramChartWidgetData } from './ChartWidgets/HistogramWidget/histogramChartWidget.types';
import { LineChartWidgetData } from './ChartWidgets/LineChartWidget/LineChartWidget.types';
import { PieChartWidgetData } from './ChartWidgets/PieChartWidget/PieChartWidget.types';
import { PyramidChartWidgetData } from './ChartWidgets/PyramidChartWidget/PyramidChartWidget.types';
import { ScatterPlotChartWidgetData } from './ChartWidgets/ScatterPlotChartWidget/ScatterPlotChartWidget.types';
import { SplineChartWidgetData } from './ChartWidgets/SplineChartWidget/SplineChartWidget.types';
import { StackedAreaChartWidgetData } from './ChartWidgets/StackedArea/StackedAreaChartWidget.types';
import { StackedBarChartWidgetData } from './ChartWidgets/StackedBar/StackedBarChartWidget.types';
import { StackedColumnChartWidgetData } from './ChartWidgets/StackedColumn/StackedColumnChartWidget.types';
import { TreeMapChartWidgetData } from './ChartWidgets/TreeMapChartWidget/TreeMapChartWidget.types';
import { WordCloudWidgetData } from './ChartWidgets/WordCloud/WordCloudWidget.types';
import { IconWidgetData } from './IconWidget/IconWidget.types';
import { ImageWidgetData } from './ImageWidget/ImageWidget.types';
import { LineWidgetData } from './Lines/LineWidget/LineWidget.types';
import { ProgressChartWidgetData } from './ProgressChartWidget/ProgressChartWidget.types';
import { QRCodeWidgetData } from './QRCodeWidget/QRCodeWidget.types';
import { LabelTextWidgetData } from './TextBasedWidgets/LabelTextWidget/LabelTextWidget.types';
import { getTableCellColors, updateTableColor } from './TextBasedWidgets/TableWidget/TableWidget.helpers';
import { TableWidgetData } from './TextBasedWidgets/TableWidget/TableWidget.types';
import {
  getFirstCharacterTextMarks,
  getFontStyleFromTextMarks,
  updateFontColor,
} from './TextBasedWidgets/TextWidget/TextWidget.helpers';
import { TextWidgetData } from './TextBasedWidgets/TextWidget/TextWidget.types';
import { AllWidgetData, MultiEditOption } from './Widget.types';

/*
 * This defines the multiple edit options that are available for each widget type
 * If a widget should support multiple edit - add it here
 */
export const MULTI_WIDGET_TOOLBAR_CONFIG = {
  [WidgetType.Line]: [MultiEditOption.Color],
  [WidgetType.Icon]: [MultiEditOption.Color],
  [WidgetType.Image]: [MultiEditOption.EditImage],
  [WidgetType.LineIcon]: [MultiEditOption.Color],
  [WidgetType.BasicShape]: [
    MultiEditOption.Color,
    MultiEditOption.BorderColor,
    MultiEditOption.BorderWidth,
    MultiEditOption.BorderStyle,
    MultiEditOption.BasicShapeType,
    MultiEditOption.Dimension,
  ],
  [WidgetType.Text]: [
    MultiEditOption.Color,
    MultiEditOption.FontFamily,
    MultiEditOption.FontSize,
    MultiEditOption.FontWeight,
    MultiEditOption.FontStyle,
    MultiEditOption.TextDecoration,
    MultiEditOption.TextAlign,
  ],
};

// widgets that have the same toolbar for single and multiple selection
export const SAME_WIDGET_BATCH = {
  [WidgetType.Image]: true,
};

/**
 * Helper functions for retrieving the colour of a widget by widget type
 */
export const GET_WIDGET_COLOR_FN_MAP: { [key: string]: (widget: AllWidgetData) => string | string[] } = {
  [WidgetType.BasicShape]: (widget) => {
    const basicShapeWidget = widget as BasicShapeWidgetData;

    // if there is a border, return the fill color and border color
    if (
      basicShapeWidget.border.color &&
      basicShapeWidget.border.width > 0 &&
      !isTransparent(basicShapeWidget.border.color)
    ) {
      return [basicShapeWidget.fillColor[0], basicShapeWidget.border.color];
    }

    return [basicShapeWidget.fillColor[0]];
  },
  [WidgetType.Image]: (widget) => {
    const imageWidget = widget as ImageWidgetData;

    if (
      imageWidget.filter &&
      imageWidget.filter.colorOverlay?.isEnabled &&
      imageWidget.filter.colorOverlay.opacity > 0
    ) {
      return imageWidget.filter.colorOverlay.color;
    }

    return '';
  },
  [WidgetType.Line]: (widget) => (widget as LineWidgetData).strokeColor,
  [WidgetType.Icon]: (widget) => {
    const iconWidget = widget as IconWidgetData;
    if (iconWidget.shapeColorTwo) {
      return [iconWidget.shapeColorOne, iconWidget.shapeColorTwo];
    }
    return [iconWidget.shapeColorOne];
  },
  [WidgetType.Text]: (widget) => {
    const textWidget = widget as TextWidgetData;
    const firstCharTextMarks = getFirstCharacterTextMarks(textWidget.proseMirrorData);
    // TODO: update this a bit
    return getFontStyleFromTextMarks(firstCharTextMarks)?.fontColor || '#000';
  },
  [WidgetType.PieChart]: (widget) => (widget as PieChartWidgetData).seriesMeta.colors,
  [WidgetType.AreaChart]: (widget) => (widget as AreaChartWidgetData).seriesMeta.colors,
  [WidgetType.ColumnChart]: (widget) => (widget as ColumnChartWidgetData).seriesMeta.colors,
  [WidgetType.ComboChart]: (widget) => (widget as ComboChartWidgetData).seriesMeta.colors,
  [WidgetType.HalfPieChart]: (widget) => (widget as HalfPieChartWidgetData).seriesMeta.colors,
  [WidgetType.HistogramChart]: (widget) => (widget as HistogramChartWidgetData).seriesMeta.colors,
  [WidgetType.StackedColumnChart]: (widget) => (widget as StackedColumnChartWidgetData).seriesMeta.colors,
  [WidgetType.FunnelChart]: (widget) => (widget as FunnelChartWidgetData).seriesMeta.colors,
  [WidgetType.PyramidChart]: (widget) => (widget as PyramidChartWidgetData).seriesMeta.colors,
  [WidgetType.ScatterPlotChart]: (widget) => (widget as ScatterPlotChartWidgetData).seriesMeta.colors,
  [WidgetType.SplineChart]: (widget) => (widget as SplineChartWidgetData).seriesMeta.colors,
  [WidgetType.AreaSplineChart]: (widget) => (widget as AreaSplineChartWidgetData).seriesMeta.colors,
  [WidgetType.TreeMapChart]: (widget) => (widget as TreeMapChartWidgetData).seriesMeta.colors,
  [WidgetType.CloudBubbleChart]: (widget) => (widget as CloudBubbleChartWidgetData).seriesMeta.colors,
  [WidgetType.StackedBarChart]: (widget) => (widget as StackedBarChartWidgetData).seriesMeta.colors,
  [WidgetType.StackedAreaChart]: (widget) => (widget as StackedAreaChartWidgetData).seriesMeta.colors,
  [WidgetType.LineChart]: (widget) => (widget as LineChartWidgetData).seriesMeta.colors,
  [WidgetType.BarChart]: (widget) => (widget as BarChartWidgetData).seriesMeta.colors,
  [WidgetType.ProgressChart]: (widget) => {
    return [(widget as ProgressChartWidgetData).dataColor, (widget as ProgressChartWidgetData).nonDataColor];
  },
  [WidgetType.LabelText]: (widget) => (widget as LabelTextWidgetData).style.color,
  [WidgetType.WordCloudChart]: (widget) => (widget as WordCloudWidgetData).seriesMeta.colors,
  [WidgetType.QRCode]: (widget) => (widget as QRCodeWidgetData).foreGroundColor,
  [WidgetType.Table]: (widget) => {
    const { proseMirrorData } = widget as TableWidgetData;
    const cellColors = getTableCellColors(proseMirrorData as JSONContent);
    return cellColors.map((color) => toHex(color));
  },
};

/**
 * Helper functions for setting the colour of a widget by widget type
 * Returns partial widget update data that can be passed into updateWidget action
 */
export const SET_WIDGET_COLOR_FN_MAP: {
  [key: string]: (
    widget: AllWidgetData,
    color: string,
    dataColors: string[],
    tableColors: { [oldCellColor: string]: { cellColor: string; textColor: string } },
    borderColor?: string,
  ) => Subset<AllWidgetData>;
} = {
  [WidgetType.BasicShape]: (widget, color, __, ___, borderColor) => {
    const basicShapeWidget = widget as BasicShapeWidgetData;
    const isTransparentFillColor = isTransparent(basicShapeWidget.fillColor[0]);

    if (borderColor) {
      if (isTransparentFillColor) {
        return { border: { color: borderColor } } as AllWidgetData;
      }

      return { fillColor: [color], border: { color: borderColor } } as AllWidgetData;
    }

    if (isTransparentFillColor) {
      return {} as AllWidgetData;
    }

    return { fillColor: [color] } as AllWidgetData;
  },
  [WidgetType.Line]: (_, color) => ({ strokeColor: color } as AllWidgetData),
  [WidgetType.Icon]: (widget, color, dataColor) => {
    if ((widget as IconWidgetData)?.type === 'Grid') {
      if (dataColor && dataColor.length) {
        if (dataColor.length === 1) {
          return { shapeColorOne: dataColor[0] } as AllWidgetData;
        }
        if (dataColor.length > 1) {
          return { shapeColorOne: dataColor[0], shapeColorTwo: dataColor[1] };
        }
      }

      console.warn('Shuffle: Data colors not provided for Statchart Icon');
      return {};
    }

    return { shapeColorOne: color } as AllWidgetData;
  },
  [WidgetType.Text]: (widget, color) => {
    const textWidgetData = widget as TextWidgetData;
    const { proseMirrorData } = textWidgetData;
    const newProseMirrorData = updateFontColor(proseMirrorData, color as string);

    return { proseMirrorData: newProseMirrorData } as AllWidgetData;
  },
  [WidgetType.Table]: (widget, _, __, tableColors) => {
    const tableWidgetData = widget as TableWidgetData;
    const { proseMirrorData } = tableWidgetData;

    if (!Object.keys(tableColors).length) {
      console.warn('Shuffle: Data colors not provided for Table');
      return {};
    }
    const newProseMirrorData = updateTableColor(proseMirrorData as JSONContent, tableColors);
    return { proseMirrorData: newProseMirrorData } as AllWidgetData;
  },
  [WidgetType.QRCode]: () => ({}),
  [WidgetType.ProgressChart]: (_, __, dataColors) => {
    if (dataColors && dataColors.length) {
      if (dataColors.length === 1) {
        return { dataColor: dataColors[0] } as AllWidgetData;
      }

      return { dataColor: dataColors[0], nonDataColor: dataColors[1] } as AllWidgetData;
    }

    console.warn('Shuffle: Data colors not provided for ProgressChart');
    return {};
  },
  [WidgetType.LabelText]: (_, color) => {
    return { style: { color } } as AllWidgetData;
  },
  [WidgetType.PieChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      seriesMeta: { colors: dataColors },
    };

    return update as PieChartWidgetData;
  },
  [WidgetType.AreaChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as AreaChartWidgetData;
  },
  [WidgetType.ColumnChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as ColumnChartWidgetData;
  },
  [WidgetType.ComboChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as ComboChartWidgetData;
  },
  [WidgetType.HalfPieChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as HalfPieChartWidgetData;
  },
  [WidgetType.HistogramChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as HistogramChartWidgetData;
  },
  [WidgetType.StackedColumnChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as StackedColumnChartWidgetData;
  },
  [WidgetType.StackedBarChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as StackedBarChartWidgetData;
  },
  [WidgetType.StackedAreaChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as StackedAreaChartWidgetData;
  },
  [WidgetType.LineChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as LineChartWidgetData;
  },
  [WidgetType.SplineChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as SplineChartWidgetData;
  },
  [WidgetType.BarChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as BarChartWidgetData;
  },
  [WidgetType.AreaSplineChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as SplineChartWidgetData;
  },
  [WidgetType.TreeMapChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color } },
      xAxis: { title: { style: { color } }, labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as TreeMapChartWidgetData;
  },
  [WidgetType.FunnelChart]: (widgetData, color, dataColors) => {
    const { dataLabels } = widgetData as FunnelChartWidgetData;
    const { inside } = dataLabels;
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color: inside ? 'contrast' : color } },
      seriesMeta: { colors: dataColors },
    };
    return update as FunnelChartWidgetData;
  },
  [WidgetType.PyramidChart]: (widgetData, color, dataColors) => {
    const { dataLabels } = widgetData as PyramidChartWidgetData;
    const { inside } = dataLabels;
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color: inside ? 'contrast' : color } },
      seriesMeta: { colors: dataColors },
    };
    return update as PyramidChartWidgetData;
  },
  [WidgetType.ScatterPlotChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as ScatterPlotChartWidgetData;
  },
  [WidgetType.CloudBubbleChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      seriesMeta: { colors: dataColors },
    };
    return update as CloudBubbleChartWidgetData;
  },
  [WidgetType.ScatterBubbleChart]: (_, color, dataColors) => {
    const update = {
      legend: { labelStyle: { color } },
      dataLabels: { style: { color } },
      yAxis: { title: { style: { color } }, labelStyle: { color }, style: { lineColor: color } },
      xAxis: { title: { style: { color } }, labelStyle: { color }, style: { lineColor: color } },
      grid: { style: { lineColor: color } },
      seriesMeta: { colors: dataColors },
    };
    return update as ScatterBubbleChartWidgetData;
  },
  [WidgetType.WordCloudChart]: (_, __, dataColors) => {
    const update = {
      seriesMeta: { colors: dataColors },
    };
    return update as WordCloudWidgetData;
  },
  [WidgetType.Image]: (_, color) => {
    return { filter: { colorOverlay: { color: color as string } } };
  },
};

/**
 * The default widget name.
 * This is used to add the default chart label on the exported pdf when there is only one widget in the design
 *
 * Reference: Related PR #582
 */
export const WIDGET_LABEL_MAP: {
  [key in Exclude<
    BasicWidgetTypes,
    WidgetType.Text | WidgetType.Table | WidgetType.QRCode | WidgetType.MapChart // TODO: TO confirm
  >]: string;
} = {
  [WidgetType.AreaChart]: 'Area chart widget',
  [WidgetType.BarChart]: 'Bar chart widget',
  [WidgetType.BasicShape]: 'Basic shape widget',
  [WidgetType.ColumnChart]: 'Column chart widget',
  [WidgetType.HalfPieChart]: 'Pie chart widget',
  [WidgetType.HistogramChart]: 'Histogram chart widget',
  [WidgetType.Icon]: 'Icon widget',
  [WidgetType.Image]: 'Image widget',
  [WidgetType.Line]: 'Line widget',
  [WidgetType.LineChart]: 'Line chart widget',
  [WidgetType.PieChart]: 'Pie chart widget',
  [WidgetType.StackedAreaChart]: 'Stacked area chart widget',
  [WidgetType.StackedBarChart]: 'Stacked bar chart widget',
  [WidgetType.StackedColumnChart]: 'Stacked column chart widget',
  [WidgetType.FunnelChart]: 'Funnel chart widget',
  [WidgetType.PyramidChart]: 'Pyramid chart widget',
  [WidgetType.ScatterPlotChart]: 'Scatter plot chart widget',
  [WidgetType.SplineChart]: 'Spline chart widget',
  [WidgetType.CloudBubbleChart]: 'Cloud bubble chart widget',
  [WidgetType.AreaSplineChart]: 'Area Spline chart widget',
  [WidgetType.ScatterBubbleChart]: 'Scatter bubble chart widget',
  [WidgetType.WordCloudChart]: 'Word cloud chart widget',
  [WidgetType.ComboChart]: 'Combo chart widget',
  [WidgetType.LineIcon]: 'Line icon widget',
  [WidgetType.VideoEmbed]: 'Video embed widget',
  [WidgetType.Lottie]: 'GIF widget',
  [WidgetType.MultipleChoices]: 'Multiple choices widget',
  [WidgetType.Form]: 'Form widget',
  [WidgetType.Signature]: 'Signature widget',
  [WidgetType.TreeMapChart]: 'Tree map chart widget',
};

/**
 * This defines the widget types that are responsive widget base types
 * (currenly only the Statchart is a responsive widget base)
 */
type ResponsiveWidgetBaseTypes = {
  [key in WidgetType]?: boolean;
};
export const RESPONSIVE_WIDGET_BASE_WIDGETS: ResponsiveWidgetBaseTypes = {
  [WidgetType.StatChart]: true,
  [WidgetType.MultipleChoices]: true,
};
export const RESPONSIVE_WIDGET_LIST = [WidgetType.StatChart, WidgetType.MultipleChoices];

/**
 * READONLY WIDGET ID PREFIX
 */
export const READONLY_WIDGET_ID_PREFIX = 'read-only-widgetbase-';

// Title id for the selected widget sidemenu
export const SIDEMENU_WIDGET_TITLE_ID = 'sidemenu-widget-title-id';

// padding applied for the bounding box lines around a widget
type BoundingBoxPadding = {
  [key in WidgetType]?: number;
};

export const WIDGET_BOUNDING_BOX_PADDING_MAP: BoundingBoxPadding = {
  // small padding to make the cursor a bit more visible when editing the text
  [WidgetType.Text]: 1,
};

export const DRAG_DROP_SUPPORTED_WIDGET_TYPES = [
  WidgetType.BasicShape,
  WidgetType.Line,
  WidgetType.Text,
  WidgetType.Icon,
  WidgetType.MapChart,
  WidgetType.Image,
  WidgetType.LineIcon,
  WidgetType.Group,
  WidgetType.Lottie,
  WidgetType.Table,
  WidgetType.MultipleChoices,
  WidgetType.Form,
  WidgetType.Signature,
  ...Object.values(ChartType),
];

export const CHART_WIDGET_TYPES = [
  WidgetType.AreaChart,
  WidgetType.PieChart,
  WidgetType.LineChart,
  WidgetType.BarChart,
  WidgetType.ColumnChart,
  WidgetType.StackedAreaChart,
  WidgetType.StackedBarChart,
  WidgetType.StackedColumnChart,
  WidgetType.HalfPieChart,
  WidgetType.ComboChart,
  WidgetType.MapChart,
  WidgetType.WordCloudChart,
  WidgetType.HistogramChart,
  WidgetType.FunnelChart,
  WidgetType.PyramidChart,
  WidgetType.ScatterPlotChart,
  WidgetType.SplineChart,
  WidgetType.CloudBubbleChart,
  WidgetType.ScatterBubbleChart,
  WidgetType.AreaSplineChart,
  WidgetType.TreeMapChart,
];

export const WIDGET_DRAG_DROP_IMAGE_GHOST_ID = 'widget-drag-drop-ghost-image';

export const INTERACTIVE_WIDGET_TYPES = [
  WidgetType.VideoEmbed,
  WidgetType.MultipleChoices,
  WidgetType.QRCode,
  WidgetType.Lottie,
  WidgetType.Table, // for checklist element
  WidgetType.MultipleChoices,
  WidgetType.Signature,
  WidgetType.Form,
];
